import { inject, Injectable } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import {
  catchError,
  concatMap,
  EMPTY,
  finalize,
  forkJoin,
  from,
  map,
  Observable,
  of,
  Subscription,
  switchMap,
  take,
  tap,
  timer,
  toArray
} from 'rxjs'
import { Selector, State, Store } from '@ngxs/store'
import {
  createEntityCollections,
  EntityDictionary
} from '@angular-ru/cdk/entity'
import {
  DataAction,
  Payload,
  StateRepository
} from '@angular-ru/ngxs/decorators'
import moment from 'moment'
import { Capacitor } from '@capacitor/core'
import { BackgroundColor, InAppBrowser } from '@capgo/capacitor-inappbrowser'
import {
  CollatableEntityCollections,
  CollatableEntityCollectionsRepository,
  defaultEntityCollation,
  EntityCollation
} from '../root-store-common'
import {
  AfterOfflineReportingMedicationAlerts,
  AfterOfflineReportingSendingPatient,
  AfterOfflineReportingSendingStatus,
  DataToBeSavedOrReported,
  OfflineReporting
} from '../../shared/model/offline-reporting'
import { StoreEventsService } from '../store-events.service'
import { NetworkService } from '../../shared/services/network.service'
import { MeasurementState } from '../measurement/measurement.state'
import { NotificationService } from '../../shared/services/notification.service'
import { OrganizationState } from '../organization/organization.state'
import { PccState } from '../pcc/pcc.state'
import { AuthState } from '../auth/auth.state'
import { ReportState } from '../report/report.state'
import { PCCStatus } from '../../shared/model/pcc.model'
import { environment } from '../../environments/environment'
import { PccWindowCommands } from '../../shared/enums/pcc-window-commands.enum'
import { WorkerService } from '../../shared/services/worker.service'
import {
  PCC_CLOSE_WINDOW_KEY,
  PCC_CLOSE_WINDOW_REASON_KEY,
  PCC_WINDOW_INTERVAL
} from '../../shared/constants/pcc-close-window-duration.constant'
import { PccCloseWindowReason } from '../../shared/enums/pcc-window-close-reason.enum'
import { LogrocketPFFacade } from '../../shared/services/logrocket-pf-facade.service'
import { PatientState } from '../patient/patient.state'
import { BackendService } from '../../shared/services/backend.service'
import { MedicationsData } from '../../shared/model/report.model'
import { LabelsState } from '../labels/labels.state'
import { OBSERVED_PATIENT_KEY_NAME } from './constants/observed-patient-key-name'
import { TIMESTAMP_KEY_NAME } from './constants/timestamp-key-name'
import { PermissionsService } from '../../shared/services/permissions.service'
import { getTotalCountOfVitalsTaken } from '../../core/helpers/get-total-count-of-vitals-taken'
import { AFTER_OFFLINE_REPORTING_VITALS_INFO } from '../../shared/constants/logrocket-track-keys'
import { PCC_MILLISECONDS_TIMEOUT } from './constants/pcc-milliseconds-timeout'
import {
  PCC_LOGIN_CHANGE_PASSWORD_FIELD_TYPE_SCRIPT,
  PCC_LOGIN_PAGE_LOAD_ERROR,
  PCC_LOGIN_RETURNING_SCRIPT
} from '../../shared/constants/federated-login.constant'
import { LogrocketTrackKeys } from '../../shared/enums/logrocket-track-keys'
import { LOGROCKET_TABS_COOKIE_NAME } from '../../shared/constants/logrocket-cookie-keys'
import { getCookieByName } from '../../core/helpers/get-cookie/get-cookie'
import { PreferenceState } from '../preference/preference.state'

const offlineReportingName = 'offline_reporting'

interface OfflineReportingCollation extends EntityCollation {
  reportAboutSentAfterRegainedInternetConnection: {
    failedToSendAmount: number
    timestamp: string
    patients: AfterOfflineReportingSendingPatient[]
  } | null
  reportAboutSentAfterRegainedInternetConnectionShown: boolean
  medicationAlertsData: AfterOfflineReportingMedicationAlerts | null
  pccSessionExpiredPopupOpened: boolean
}

@StateRepository()
@State<
  CollatableEntityCollections<OfflineReporting, OfflineReportingCollation>
>({
  name: offlineReportingName,
  defaults: {
    ...createEntityCollections(),
    ...defaultEntityCollation(),
    reportAboutSentAfterRegainedInternetConnection: null,
    reportAboutSentAfterRegainedInternetConnectionShown: false,
    medicationAlertsData: null,
    pccSessionExpiredPopupOpened: false
  }
})
@Injectable()
export class OfflineReportingState extends CollatableEntityCollectionsRepository<
  OfflineReporting,
  OfflineReportingCollation
> {
  private readonly backendService = inject(BackendService)
  private readonly storeEvents = inject(StoreEventsService)
  private readonly networkService = inject(NetworkService)
  private readonly notificationService = inject(NotificationService)
  private readonly workerService = inject(WorkerService)
  private readonly permissionsService = inject(PermissionsService)
  private readonly logrocketPFFacade = inject(LogrocketPFFacade)
  private readonly measurementState = inject(MeasurementState)
  private readonly store = inject(Store)
  private readonly reportState = inject(ReportState)
  private readonly pccState = inject(PccState)
  private readonly authState = inject(AuthState)

  private openedWindow: Window | null = null
  private openedWindowInterval: NodeJS.Timeout
  private aiomedPccChannel: BroadcastChannel
  private setReportAboutSentAfterRegainedInternetConnectionShownSubscription: Subscription | null =
    null

  @Selector()
  public static offlineReportingPatientsIds(
    state: CollatableEntityCollections<
      OfflineReporting,
      OfflineReportingCollation
    >
  ) {
    // due to saved in localStorage of just entities which keys are exactly ids of patients
    const entitiesIds = Object.keys(state.entities)
    const patientsIdsFailedToBeSent =
      state.reportAboutSentAfterRegainedInternetConnection?.patients.map(
        patient => patient.id
      ) || []
    return [...new Set([...entitiesIds, ...patientsIdsFailedToBeSent])]
  }

  @Selector()
  public static offlineReportingPatientsMeasurements(
    state: CollatableEntityCollections<
      OfflineReporting,
      OfflineReportingCollation
    >
  ): EntityDictionary<string, OfflineReporting> {
    const patientsFailedToBeSent: Record<string, OfflineReporting> =
      state.reportAboutSentAfterRegainedInternetConnection?.patients.reduce(
        (accum, current) => {
          accum[current.id] = {
            id: current.id,
            dataToBeReported: current.data.dataToBeReported || {},
            deviceEntriesToBeSaved: current.data.deviceEntriesToBeSaved || {},
            manualEntriesToBeSaved: current.data.manualEntriesToBeSaved || {}
          }
          return accum
        },
        {} as Record<string, OfflineReporting>
      ) || {}

    return { ...state.entities, ...patientsFailedToBeSent }
  }

  @Selector()
  public static reportAboutSentAfterRegainedInternetConnection(
    state: CollatableEntityCollections<
      OfflineReporting,
      OfflineReportingCollation
    >
  ) {
    return state.reportAboutSentAfterRegainedInternetConnection
  }

  @Selector()
  public static reportAboutSentAfterRegainedInternetConnectionShown(
    state: CollatableEntityCollections<
      OfflineReporting,
      OfflineReportingCollation
    >
  ) {
    return state.reportAboutSentAfterRegainedInternetConnectionShown
  }

  @Selector()
  public static medicationAlertsData(
    state: CollatableEntityCollections<
      OfflineReporting,
      OfflineReportingCollation
    >
  ) {
    return state.medicationAlertsData
  }

  @Selector()
  public static pccSessionExpiredPopupOpened(
    state: CollatableEntityCollections<
      OfflineReporting,
      OfflineReportingCollation
    >
  ) {
    return state.pccSessionExpiredPopupOpened
  }

  constructor() {
    super()
    this.detectOnlineFromOfflineChanges()
  }

  public override ngxsOnInit(): void {
    this.storeEvents.logout$
      .pipe(
        tap(() => {
          this.resetWithoutEntitiesStateButRemovedDataToBeReported()
        })
      )
      .subscribe()
  }

  @DataAction()
  addPatientWithVitalsDataToBeSavedOrAndReported(data: OfflineReporting) {
    const patientData = this.getState().entities[data.id]
    const updatedData = {
      ...patientData,
      id: data.id,
      dataToBeReported: {
        ...(patientData?.dataToBeReported || {}),
        ...(data?.dataToBeReported || {})
      },
      deviceEntriesToBeSaved: {
        ...(patientData?.deviceEntriesToBeSaved || {}),
        ...(data?.deviceEntriesToBeSaved || {})
      },
      manualEntriesToBeSaved: {
        ...(patientData?.manualEntriesToBeSaved || {}),
        ...(data?.manualEntriesToBeSaved || {})
      }
    }
    this.upsertOne(updatedData)
  }

  @DataAction()
  removePatientsFromStore(@Payload('ids') ids: string[]): void {
    this.removeMany(ids)
  }

  @DataAction()
  resetState(): void {
    this.reset()
  }

  @DataAction()
  resetReportPartOfState(): void {
    this.ctx.patchState({
      reportAboutSentAfterRegainedInternetConnection: null,
      reportAboutSentAfterRegainedInternetConnectionShown: false
    })
  }

  @DataAction()
  setReportAboutSentAfterRegainedInternetConnectionShown(
    @Payload('reportAboutSentAfterRegainedInternetConnectionShown')
    reportAboutSentAfterRegainedInternetConnectionShown: boolean
  ): void {
    this.ctx.patchState({
      reportAboutSentAfterRegainedInternetConnectionShown
    })
  }

  @DataAction()
  resetWithoutEntitiesStateButRemovedDataToBeReported(): void {
    const entitiesToBeResend = Object.fromEntries(
      Object.entries(this.ctx.getState().entities)
        .filter(
          ([key, value]) =>
            (value.deviceEntriesToBeSaved &&
              Object.keys(value.deviceEntriesToBeSaved).length) ||
            (value.manualEntriesToBeSaved &&
              Object.keys(value.manualEntriesToBeSaved).length)
        )
        .map(([key, value]) => [key, { ...value, dataToBeReported: {} }])
    )

    this.ctx.patchState({
      reportAboutSentAfterRegainedInternetConnection: null,
      reportAboutSentAfterRegainedInternetConnectionShown: false,
      medicationAlertsData: null,
      ids: [],
      entities: entitiesToBeResend
    })
  }

  @DataAction()
  addPatientWithVitalsDataToBePresentedInReport(data: OfflineReporting): void {
    const patient = this.store
      .selectSnapshot(PatientState.patients)
      .find(patient => patient.id === data.id)

    if (!patient) {
      return
    }

    const patientToBeAdded: AfterOfflineReportingSendingPatient = {
      id: data.id,
      avatarUrl: patient.avatar,
      room: patient.room,
      firstName: patient.firstName,
      lastName: patient.lastName,
      data,
      reportingStatus: 'initial'
    }

    const canReportVitalsToEMR = this.permissionsService.canReportVitalsToEMR()

    if (
      this.setReportAboutSentAfterRegainedInternetConnectionShownSubscription
    ) {
      this.setReportAboutSentAfterRegainedInternetConnectionShownSubscription.unsubscribe()
    }

    this.setReportAboutSentAfterRegainedInternetConnectionShownSubscription =
      timer(canReportVitalsToEMR ? PCC_MILLISECONDS_TIMEOUT : 0)
        .pipe(take(1))
        .subscribe(() => {
          this.setReportAboutSentAfterRegainedInternetConnectionShown(true)
        })

    const reportAboutSentAfterRegainedInternetConnection =
      this.getState().reportAboutSentAfterRegainedInternetConnection

    if (!reportAboutSentAfterRegainedInternetConnection) {
      this.ctx.patchState({
        reportAboutSentAfterRegainedInternetConnection: {
          failedToSendAmount: 1,
          timestamp: new Date().toISOString(),
          patients: [patientToBeAdded]
        }
      })
      return
    }

    const patients = [
      ...reportAboutSentAfterRegainedInternetConnection.patients,
      patientToBeAdded
    ]

    this.ctx.patchState({
      reportAboutSentAfterRegainedInternetConnection: {
        failedToSendAmount:
          reportAboutSentAfterRegainedInternetConnection.failedToSendAmount + 1,
        timestamp: new Date().toISOString(),
        patients
      }
    })
  }

  @DataAction()
  updatePatientWithVitalsDataToBePresentedInReport(
    id: string,
    status: AfterOfflineReportingSendingStatus
  ): void {
    const reportAboutSentAfterRegainedInternetConnection =
      this.getState().reportAboutSentAfterRegainedInternetConnection

    if (!reportAboutSentAfterRegainedInternetConnection) {
      return
    }

    if (
      !reportAboutSentAfterRegainedInternetConnection.patients.some(
        patient => patient.id === id
      )
    ) {
      return
    }

    const updatedPatients =
      reportAboutSentAfterRegainedInternetConnection.patients.map(patient =>
        patient.id === id ? { ...patient, reportingStatus: status } : patient
      )

    this.ctx.patchState({
      reportAboutSentAfterRegainedInternetConnection: {
        ...reportAboutSentAfterRegainedInternetConnection,
        patients: updatedPatients as AfterOfflineReportingSendingPatient[]
      }
    })
  }

  @DataAction()
  retryOnePatientFromReport(id: string) {
    const reportAboutSentAfterRegainedInternetConnection =
      this.getState().reportAboutSentAfterRegainedInternetConnection

    if (!reportAboutSentAfterRegainedInternetConnection) {
      return
    }

    const patientToRetry =
      reportAboutSentAfterRegainedInternetConnection.patients.find(
        patient => patient.id === id
      )

    if (!patientToRetry) {
      return
    }

    this.updatePatientWithVitalsDataToBePresentedInReport(
      patientToRetry.id,
      'sending'
    )

    const devicesWithTimestamps = Object.entries(
      patientToRetry.data.deviceEntriesToBeSaved!
    )
      .map(([deviceId, timestampData]) =>
        Object.keys(timestampData).map(timestamp => ({
          deviceId,
          timestamp
        }))
      )
      .flat(2)

    const data = {
      [OBSERVED_PATIENT_KEY_NAME]: patientToRetry.data.id,
      deviceData: devicesWithTimestamps.map(({ deviceId, timestamp }) => {
        return {
          [OBSERVED_PATIENT_KEY_NAME]: patientToRetry.id,
          deviceId,
          ...(patientToRetry.data.deviceEntriesToBeSaved![deviceId]?.[
            timestamp
          ] || {}),
          timestamp
        }
      }),
      manualData: {
        [OBSERVED_PATIENT_KEY_NAME]: patientToRetry.data.id,
        ...patientToRetry.data.manualEntriesToBeSaved
      },
      dataToBeReported: patientToRetry.data.dataToBeReported
    }

    const dataForPublishVitals = data.deviceData.length
      ? data.deviceData.map(deviceData =>
          this.measurementState.publishVitals(deviceData)
        )
      : [of(1)]
    return from(dataForPublishVitals)
      .pipe(
        concatMap(req$ => req$),
        toArray(),
        map(() => data),
        switchMap(patientData => {
          if (
            Object.keys(patientData.manualData).filter(
              key =>
                key !== OBSERVED_PATIENT_KEY_NAME && key !== TIMESTAMP_KEY_NAME
            ).length > 0
          ) {
            return this.measurementState
              .publishVitals(patientData.manualData)
              .pipe(map(() => patientData))
          }
          return of(patientData)
        }),
        switchMap(patientData => {
          if (
            patientData.dataToBeReported &&
            Object.keys(patientData.dataToBeReported).length > 0
          ) {
            return this.pccState
              .updatePccEmrMeasurement(
                {
                  [OBSERVED_PATIENT_KEY_NAME]:
                    patientData.dataToBeReported.observedPatient!,
                  bloodPressureMethod:
                    patientData.dataToBeReported.bloodPressureMethod!,
                  bodyTemperatureMethod:
                    patientData.dataToBeReported.bodyTemperatureMethod!,
                  heartRateMethod:
                    patientData.dataToBeReported.heartRateMethod!,
                  spo2Method: patientData.dataToBeReported.spo2Method!,
                  ...patientData.dataToBeReported
                },
                undefined,
                false
              )
              .pipe(
                tap(patientExport => {
                  this.pccState.resetPccDetailLoading()
                  this.updatePatientWithVitalsDataToBePresentedInReport(
                    patientToRetry.id,
                    patientExport[0].exportStatus === 'COMPLETED'
                      ? 'success'
                      : 'failed'
                  )
                }),
                map(() => patientData),
                catchError(er => {
                  this.updatePatientWithVitalsDataToBePresentedInReport(
                    patientToRetry.id,
                    'failed'
                  )
                  return of(patientData)
                })
              )
          }
          this.updatePatientWithVitalsDataToBePresentedInReport(
            patientToRetry.id,
            'success'
          )
          return of(patientData)
        }),
        catchError(() => {
          this.updatePatientWithVitalsDataToBePresentedInReport(
            patientToRetry.id,
            'failed'
          )
          return EMPTY
        })
      )
      .subscribe()
  }

  @DataAction()
  getMedicationPharmacyOrders(@Payload('data') data: MedicationsData[]) {
    const organization = this.store.selectSnapshot(
      OrganizationState.currentOrganization
    )
    return this.backendService
      .getMedicationBulk(data, organization?.id || '')
      .pipe(
        tap(res => {
          if (!res.filter(data => data.alerts.length).length) {
            return
          }

          const patients = this.store.selectSnapshot(PatientState.patients)
          const medicationAlertsData: AfterOfflineReportingMedicationAlerts =
            res
              .map(patientData => {
                const patient = patients.find(
                  patient => patient.id === patientData.patientId
                )
                if (!patient) {
                  return null
                }
                return {
                  id: patient.id,
                  firstName: patient.firstName,
                  lastName: patient.lastName,
                  avatarUrl: patient.avatar,
                  room: patient.room,
                  alerts: patientData.alerts
                }
              })
              .filter(patientData => patientData !== null)

          this.ctx.patchState({ medicationAlertsData })
        })
      )
  }

  @DataAction()
  public closeMedicationAlertsAfterOffline(): void {
    this.ctx.patchState({ medicationAlertsData: null })
  }

  public retryBulkPatientsFromReport(): void {
    const reportAboutSentAfterRegainedInternetConnection =
      this.getState().reportAboutSentAfterRegainedInternetConnection
    if (!reportAboutSentAfterRegainedInternetConnection) {
      return
    }

    const patientIdsToRetry =
      reportAboutSentAfterRegainedInternetConnection.patients
        .filter(patient => patient.reportingStatus === 'initial')
        .map(patient => patient.id)

    patientIdsToRetry.forEach(id => {
      this.updatePatientWithVitalsDataToBePresentedInReport(id, 'sending')
      this.retryOnePatientFromReport(id)
    })
  }

  public allPatientsSuccessfullySent(successfullySentAmount: number): void {
    this.resetReportPartOfState()
    const displayedEmrName = this.store.selectSnapshot(
      OrganizationState.emrConfig
    ).displayedName
    const patientsLabels = this.store.selectSnapshot(LabelsState.patientLabels)
    const canReportVitalsToEMR = this.permissionsService.canReportVitalsToEMR()
    this.notificationService.success(
      `${successfullySentAmount} ${successfullySentAmount === 1 ? patientsLabels.singular : patientsLabels.plural} successfully ${canReportVitalsToEMR ? 'sent to ' + displayedEmrName : 'saved'} `
    )
  }

  public handlePccOpenedWindowFurtherReporting(): void {
    this.ctx.patchState({ pccSessionExpiredPopupOpened: false })
    this.pccBroadcastChannelListener()
    this.handleTabletPccLoginBasedOnDeviceType()
    this.handleTabletPccLoginListeners()
    clearInterval(this.openedWindowInterval)
    this.workerService.startWindowOpenedAutoClosingSession()
    this.openedWindowInterval = setInterval(() => {
      if (this.openedWindow && this.openedWindow.closed) {
        clearInterval(this.openedWindowInterval)
        this.logrocketPFFacade.logrocketTrack(PCC_CLOSE_WINDOW_KEY, {
          [PCC_CLOSE_WINDOW_REASON_KEY]:
            PccCloseWindowReason.PccTabWasClosedEitherAfterSuccessfulLoginOrJustClosed
        })
      }
    }, PCC_WINDOW_INTERVAL)
  }

  private sendMeasurementsBulk(isTokenAlreadyChecked: boolean = false): void {
    const entities = this.getState().entities
    if (!Object.keys(entities).length) {
      return
    }

    const canReportVitalsToEMR = this.permissionsService.canReportVitalsToEMR()
    const todayStartOfDay = moment().startOf('day').toISOString()

    const data: DataToBeSavedOrReported[] = Object.entries(entities)
      .map(([key, value]) => {
        const devicesWithTimestamps = Object.entries(
          value.deviceEntriesToBeSaved!
        )
          .map(([deviceId, timestampData]) =>
            Object.keys(timestampData)
              .filter(timestamp => {
                const timestamptStartOfDay = moment(timestamp)
                  .startOf('day')
                  .toISOString()
                return timestamptStartOfDay === todayStartOfDay
              })
              .map(timestamp => ({
                deviceId,
                timestamp
              }))
          )
          .flat(2)

        return {
          [OBSERVED_PATIENT_KEY_NAME]: value.id,
          deviceData: devicesWithTimestamps.map(({ deviceId, timestamp }) => {
            return {
              [OBSERVED_PATIENT_KEY_NAME]: value.id,
              deviceId,
              ...(value.deviceEntriesToBeSaved![deviceId]?.[timestamp] || {}),
              timestamp: timestamp || new Date().toISOString()
            }
          }),
          manualData: {
            [OBSERVED_PATIENT_KEY_NAME]: value.id,
            ...value.manualEntriesToBeSaved
          },
          dataToBeReported: value.dataToBeReported
        }
      })
      .flat()
    let successfullySentAmount = 0
    let successfullSavedVitalsAmount = 0
    let successfullyReportedAmount = 0
    let successfullReportedVitalsAmount = 0
    let processedPatientsDataLength = 0

    const isPccTokenValid$ =
      canReportVitalsToEMR && !isTokenAlreadyChecked
        ? this.pccState.getPccIsAuthStatus(true)
        : of('VALID')

    const patientsFailedToBeSent: OfflineReporting[] = []

    const dataForGettingMedicationsAlertsData: MedicationsData[] = data
      .filter(
        item =>
          !!item.dataToBeReported &&
          Object.keys(item.dataToBeReported).filter(
            key =>
              key !== OBSERVED_PATIENT_KEY_NAME && key !== TIMESTAMP_KEY_NAME
          ).length
      )
      .map(item => {
        return {
          patientId: item.dataToBeReported?.observedPatient!,
          systolicPressure: item.dataToBeReported?.systolicPressure,
          diastolicPressure: item.dataToBeReported?.diastolicPressure,
          heartRate: item.dataToBeReported?.heartRate,
          spo2: item.dataToBeReported?.spo2,
          bodyTemperature: item.dataToBeReported?.bodyTemperature,
          respirationRate: item.dataToBeReported?.respirationRate,
          painLevel: item.dataToBeReported?.painLevel
        }
      })

    const hasAllDataWithoutOpportunityToBeReported = data.filter(
      item =>
        item.dataToBeReported && !Object.keys(item.dataToBeReported).length
    )

    isPccTokenValid$
      .pipe(
        switchMap(token => {
          if (token !== 'VALID') {
            this.ctx.patchState({ pccSessionExpiredPopupOpened: true })
            return of(1)
          }
          return from(data)
            .pipe(
              concatMap(patientData => {
                const manualTimestampIsToday =
                  moment(patientData.manualData.timestamp)
                    .startOf('day')
                    .toISOString() === todayStartOfDay

                const dataForPublishVitals = patientData.deviceData.length
                  ? patientData.deviceData.map(deviceData =>
                      this.measurementState.publishVitals(deviceData)
                    )
                  : [of(1)]
                return from(dataForPublishVitals).pipe(
                  concatMap(req$ => req$),
                  toArray(),
                  map(d => {
                    const deviceEntriesAmount = patientData.deviceData.reduce(
                      (prev, next) =>
                        (prev += getTotalCountOfVitalsTaken(next)),
                      0
                    )
                    successfullSavedVitalsAmount += deviceEntriesAmount

                    return patientData
                  }),
                  switchMap(patientData => {
                    if (
                      Object.keys(patientData.manualData).filter(
                        key =>
                          key !== OBSERVED_PATIENT_KEY_NAME &&
                          key !== TIMESTAMP_KEY_NAME
                      ).length > 0 &&
                      manualTimestampIsToday
                    ) {
                      return this.measurementState
                        .publishVitals(patientData.manualData)
                        .pipe(
                          map(d => {
                            const manualEntriesAmount =
                              getTotalCountOfVitalsTaken(patientData.manualData)

                            successfullSavedVitalsAmount += manualEntriesAmount
                            return patientData
                          })
                        )
                    }
                    return of(patientData)
                  }),
                  switchMap(patientData => {
                    if (
                      patientData.dataToBeReported &&
                      Object.keys(patientData.dataToBeReported).length > 0
                    ) {
                      return this.pccState
                        .updatePccEmrMeasurement(
                          {
                            [OBSERVED_PATIENT_KEY_NAME]:
                              patientData.dataToBeReported.observedPatient!,
                            bloodPressureMethod:
                              patientData.dataToBeReported.bloodPressureMethod!,
                            bodyTemperatureMethod:
                              patientData.dataToBeReported
                                .bodyTemperatureMethod!,
                            heartRateMethod:
                              patientData.dataToBeReported.heartRateMethod!,
                            spo2Method:
                              patientData.dataToBeReported.spo2Method!,
                            ...patientData.dataToBeReported
                          },
                          undefined,
                          false
                        )
                        .pipe(
                          tap(patientExport => {
                            const exp = patientExport[0]
                            if (
                              exp &&
                              exp.exportStatus === PCCStatus.Completed
                            ) {
                              const exportedVitalsAmount =
                                getTotalCountOfVitalsTaken(
                                  exp.observationExport
                                )
                              successfullReportedVitalsAmount +=
                                exportedVitalsAmount
                            }
                            this.pccState.resetPccDetailLoading()
                            if (
                              patientExport[0]?.exportStatus ===
                              PCCStatus.Completed
                            ) {
                              successfullyReportedAmount++
                            } else {
                              patientsFailedToBeSent.push(
                                entities[patientData.observedPatient]
                              )
                            }
                          }),
                          map(() => patientData),
                          catchError(() => {
                            return of(patientData)
                          })
                        )
                    }
                    return of(patientData)
                  }),
                  catchError(() => {
                    patientsFailedToBeSent.push(
                      entities[patientData.observedPatient]
                    )
                    return of(patientData)
                  }),
                  tap(patientData => {
                    processedPatientsDataLength++
                    if (
                      patientData.deviceData.length ||
                      (Object.keys(patientData.manualData).filter(
                        key =>
                          key !== OBSERVED_PATIENT_KEY_NAME &&
                          key !== TIMESTAMP_KEY_NAME
                      ).length &&
                        manualTimestampIsToday)
                    ) {
                      successfullySentAmount++
                    }
                  })
                )
              })
            )
            .pipe(
              tap(() => {
                this.reportState.setIsWarningAboutNotSavedOrSentMeasurementsVisible(
                  false
                )
                const patientsLabels = this.store.selectSnapshot(
                  LabelsState.patientLabels
                )
                this.reportState.setSuccessfullySentCurrentShiftPatientAllMeasurements(
                  null
                )
                const displayedEmrName = this.store.selectSnapshot(
                  OrganizationState.emrConfig
                ).displayedName
                const isPatientCardOpened = this.store.selectSnapshot(
                  ReportState.isPatientVitalsTakenPopupVisible
                )

                if (processedPatientsDataLength === data.length) {
                  this.sendCollectedLocallyVitalsDataToLogRocket(
                    data,
                    successfullSavedVitalsAmount,
                    successfullReportedVitalsAmount
                  )
                }
                if (
                  successfullyReportedAmount > 0 &&
                  processedPatientsDataLength === data.length &&
                  canReportVitalsToEMR &&
                  !isPatientCardOpened
                ) {
                  this.notificationService.success(
                    `${successfullyReportedAmount} ${successfullyReportedAmount === 1 ? patientsLabels.singular : patientsLabels.plural} successfully sent to ${displayedEmrName}`
                  )
                } else if (
                  successfullySentAmount > 0 &&
                  processedPatientsDataLength === data.length &&
                  (!canReportVitalsToEMR ||
                    hasAllDataWithoutOpportunityToBeReported) &&
                  !isPatientCardOpened &&
                  !patientsFailedToBeSent.length
                ) {
                  this.notificationService.success(
                    `${successfullySentAmount} ${successfullySentAmount === 1 ? patientsLabels.singular : patientsLabels.plural} successfully saved`
                  )
                }

                if (
                  canReportVitalsToEMR &&
                  processedPatientsDataLength === data.length
                ) {
                  this.getMedicationPharmacyOrders(
                    dataForGettingMedicationsAlertsData
                  )
                }

                if (
                  patientsFailedToBeSent.length &&
                  processedPatientsDataLength === data.length
                ) {
                  patientsFailedToBeSent.forEach(patientData => {
                    if (
                      this.getState().reportAboutSentAfterRegainedInternetConnection?.patients.some(
                        patient => patient.id === patientData.id
                      )
                    ) {
                      return
                    }

                    this.addPatientWithVitalsDataToBePresentedInReport(
                      patientData
                    )
                  })
                }
              }),
              catchError(() => {
                this.reportState.setIsWarningAboutNotSavedOrSentMeasurementsVisible(
                  false
                )
                if (
                  patientsFailedToBeSent.length &&
                  processedPatientsDataLength === data.length
                ) {
                  if (canReportVitalsToEMR) {
                    this.getMedicationPharmacyOrders(
                      dataForGettingMedicationsAlertsData
                    )
                  }
                  patientsFailedToBeSent.forEach(patientData => {
                    if (
                      this.getState().reportAboutSentAfterRegainedInternetConnection?.patients.some(
                        patient => patient.id === patientData.id
                      )
                    ) {
                      return
                    }

                    this.addPatientWithVitalsDataToBePresentedInReport(
                      patientData
                    )
                  })
                }
                return EMPTY
              }),
              finalize(() => {
                this.removePatientsFromStore([
                  ...new Set(data.map(d => d.observedPatient))
                ])
              })
            )
        })
      )
      .subscribe()
  }

  private detectOnlineFromOfflineChanges(): void {
    this.networkService.isOnlineObs$
      .pipe(
        switchMap(() => this.storeEvents.getMeasurementSummary$.pipe(take(1))),
        takeUntilDestroyed()
      )
      .subscribe(isOnline => {
        if (isOnline) {
          this.sendMeasurementsBulk()
        }
      })
  }

  private handleTabletPccLoginBasedOnDeviceType(): void {
    const url = `${environment.authApiUrl}/pcc/login?return_url=${
      window.location.origin
    }/reports?loading=true&token=${this.authState.getState().accessJwt?.token}`
    if (Capacitor.isNativePlatform()) {
      InAppBrowser.openWebView({
        url
      })
      this.openedWindow = {
        closed: false
      } as any
    } else {
      this.openedWindow = window.open(url)
    }
  }

  private handleTabletPccLoginListeners(): void {
    const preferenceDepartment = this.store.selectSnapshot(
      PreferenceState.preferenceDepartment
    )
    const organization_pcc_prefix =
      (preferenceDepartment &&
        !Array.isArray(preferenceDepartment) &&
        preferenceDepartment._ownerOrganization?.organization_pcc_prefix) ||
      (Array.isArray(preferenceDepartment) &&
        preferenceDepartment[0]?._ownerOrganization?.organization_pcc_prefix) ||
      undefined

    if (!Capacitor.isNativePlatform()) {
      return
    }

    InAppBrowser.clearAllCookies()
    InAppBrowser.removeAllListeners()

    InAppBrowser.addListener('closeEvent', e => {
      if (!this.openedWindow?.closed) {
        ;(this.openedWindow as any).closed = true
      }
    })

    const cookieName = LOGROCKET_TABS_COOKIE_NAME
    const cookieValue = getCookieByName(cookieName)
    const cookieToSet = `${decodeURIComponent(cookieName)}=${cookieValue}`
    InAppBrowser.addListener('browserPageLoaded', () => {
      InAppBrowser.executeScript({
        code: PCC_LOGIN_CHANGE_PASSWORD_FIELD_TYPE_SCRIPT(
          organization_pcc_prefix,
          cookieToSet
        )
      })
    })
    InAppBrowser.addListener('pageLoadError', () => {
      console.warn('PAGE LOAD ERRORED')
      this.networkService.isOnlineObs$.pipe(take(1)).subscribe(isOnline => {
        if (isOnline) {
          InAppBrowser.executeScript({
            code: PCC_LOGIN_PAGE_LOAD_ERROR()
          })
        } else {
          InAppBrowser.close()
        }
      })
    })

    InAppBrowser.addListener('messageFromWebview', e => {
      if ((e as any)?.hasPccLoginError) {
        this.logrocketPFFacade.logrocketTrack(
          LogrocketTrackKeys.PccLoginFailedUsernameOrPassword,
          { isTablet: true }
        )
      } else {
        console.warn('message from webview', e)
      }
    })

    InAppBrowser.addListener('urlChangeEvent', e => {
      const origin = window.location.origin
      const isAiomedUserLoggedAndPCCWindowWasOpened = e.url.includes(
        `${origin}/reports?loading=true`
      )
      if (
        (e.url.includes('error') && e.url.includes(origin)) ||
        isAiomedUserLoggedAndPCCWindowWasOpened
      ) {
        const bc = new BroadcastChannel(PccWindowCommands.PccAiomedChannel)
        InAppBrowser.executeScript({
          code: PCC_LOGIN_RETURNING_SCRIPT
        })
        bc.postMessage({
          type: PccWindowCommands.PccAiomedChannelLoggedIn,
          url: e.url
        })
        InAppBrowser.close()
      }
    })
  }

  private pccBroadcastChannelListener() {
    this.aiomedPccChannel = new BroadcastChannel(
      PccWindowCommands.PccAiomedChannel
    )
    this.aiomedPccChannel.onmessage = (ev: any) => {
      const type = Capacitor.isNativePlatform() ? ev.data.type : ev.data
      if (type === PccWindowCommands.PccAiomedChannelLoggedIn) {
        if (!this.openedWindow) return
        if (this.openedWindow && !this.openedWindow.closed) {
          this.sendMeasurementsBulk(true)
        }
        this.closePCCOpenedWindowBasedOnDeviceType()
        this.logrocketPFFacade.logrocketTrack(PCC_CLOSE_WINDOW_KEY, {
          [PCC_CLOSE_WINDOW_REASON_KEY]: PccCloseWindowReason.SuccessfullLogin
        })
      }
    }
  }

  private closePCCOpenedWindowBasedOnDeviceType(): void {
    if (Capacitor.isNativePlatform()) {
      InAppBrowser.close()
    } else {
      this.openedWindow?.close()
    }
  }

  private sendCollectedLocallyVitalsDataToLogRocket(
    data: DataToBeSavedOrReported[],
    successfullySavedVitalsAmount: number,
    successfullyReportedVitalsAmount: number
  ): void {
    let savedLocallyVitalsTotalAmount = 0
    data.forEach(item => {
      savedLocallyVitalsTotalAmount += getTotalCountOfVitalsTaken(
        item.manualData
      )

      item.deviceData.forEach(deviceVitals => {
        savedLocallyVitalsTotalAmount +=
          getTotalCountOfVitalsTaken(deviceVitals)
      })
    })

    this.logrocketPFFacade.logrocketTrack(AFTER_OFFLINE_REPORTING_VITALS_INFO, {
      savedLocallyVitalsTotalAmount,
      successfullySavedVitalsAmount,
      successfullyReportedVitalsAmount
    })
  }

  protected override loadEntitiesFromBackend(ids?: string[]): Observable<any> {
    return EMPTY
  }

  protected override setPaginationSetting(type?: string): Observable<any> {
    return EMPTY
  }
}
